/**
 * TanStack Query hooks для dataset endpoints (Phase 2: File Loader).
 *
 * Покрывает:
 * - useUploadDataset
 * - useProjectDatasets
 * - useDataset
 * - useDatasetPreview
 * - useDatasetSheets
 * - useSelectSheet
 * - useRenameColumns
 * - useSelectColumns
 * - useColumnPreview
 * - useReparseDataset
 * - useDeleteDataset
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import type { AxiosProgressEvent } from 'axios';

import api from '@/lib/api';

// === Types ===

export type DatasetStatus =
  | 'uploaded'
  | 'parsing'
  | 'ready'
  | 'failed';

export type DatasetFileType = 'xlsx' | 'xls' | 'csv' | 'docx' | 'pdf';

export interface Dataset {
  id: string;
  project_id: string;
  name: string;
  original_filename: string;
  file_type: DatasetFileType;
  file_size_bytes: number;
  status: DatasetStatus;
  error_message: string | null;
  total_rows: number;
  total_columns: number;
  raw_metadata: Record<string, unknown> | null;
  column_renames: Record<string, string> | null;
  selected_columns: string[] | null;
  created_at: string;
  updated_at: string | null;
}

export interface DatasetRowPreview {
  id: string;
  row_index: number;
  raw_data: Record<string, unknown>;
  status: string;
  error_message: string | null;
}

export interface DatasetPreviewResponse {
  dataset: Dataset;
  rows: DatasetRowPreview[];
  limit: number;
  offset: number;
  requires_sheet_selection: boolean;
  available_sheets: SheetGroup[];
}

export interface SheetGroup {
  label: string;
  members: string[];
  aliases: string[];
}

export interface SheetGroupingResponse {
  groups: SheetGroup[];
  suggested_label: string | null;
}

export interface ColumnPreviewResponse {
  dataset_id: string;
  effective_columns: string[];
  rows: { raw_data: Record<string, unknown>; displayed_data: Record<string, unknown> }[];
  limit: number;
}

export interface UploadResponse {
  dataset_id: string;
  status: DatasetStatus;
  file_type: DatasetFileType;
  file_size_bytes: number;
  original_filename: string;
}

// === Project datasets ===

export function useProjectDatasets(
  projectId: string | null,
  options?: Partial<UseQueryOptions<{ items: Dataset[]; total: number }>>,
) {
  return useQuery<{ items: Dataset[]; total: number }>({
    queryKey: ['datasets', 'project', projectId],
    queryFn: async () =>
      (await api.get(`/projects/${projectId}/datasets`)).data,
    enabled: Boolean(projectId),
    refetchInterval: (q) => {
      // Auto-refresh пока есть status in (uploaded, parsing)
      const data = q.state.data as { items: Dataset[] } | undefined;
      if (!data) return false;
      const hasInflight = data.items.some(
        (d) => d.status === 'uploaded' || d.status === 'parsing',
      );
      return hasInflight ? 3_000 : false;
    },
    ...options,
  });
}

// === Single dataset ===

export function useDataset(
  datasetId: string | null,
  options?: Partial<UseQueryOptions<Dataset>>,
) {
  return useQuery<Dataset>({
    queryKey: ['datasets', datasetId],
    queryFn: async () => (await api.get<Dataset>(`/datasets/${datasetId}`)).data,
    enabled: Boolean(datasetId),
    refetchInterval: (q) => {
      const data = q.state.data as Dataset | undefined;
      if (!data) return false;
      return data.status === 'parsing' || data.status === 'uploaded' ? 2_000 : false;
    },
    ...options,
  });
}

// === Preview ===

export function useDatasetPreview(
  datasetId: string | null,
  options?: Partial<UseQueryOptions<DatasetPreviewResponse>>,
) {
  return useQuery<DatasetPreviewResponse>({
    queryKey: ['datasets', datasetId, 'preview'],
    queryFn: async () =>
      (await api.get<DatasetPreviewResponse>(`/datasets/${datasetId}/preview`)).data,
    enabled: Boolean(datasetId),
    ...options,
  });
}

// === Sheets ===

export function useDatasetSheets(
  datasetId: string | null,
  options?: Partial<UseQueryOptions<SheetGroupingResponse>>,
) {
  return useQuery<SheetGroupingResponse>({
    queryKey: ['datasets', datasetId, 'sheets'],
    queryFn: async () =>
      (await api.get<SheetGroupingResponse>(`/datasets/${datasetId}/sheets`)).data,
    enabled: Boolean(datasetId),
    ...options,
  });
}

// === Column preview (after renames) ===

export type ColumnPreviewLimit = 20 | 50 | 100 | 0; // 0 = all (Phase 2.4)

export function useColumnPreview(
  datasetId: string | null,
  limit: ColumnPreviewLimit | number = 20,
  options?: Partial<UseQueryOptions<ColumnPreviewResponse>>,
) {
  return useQuery<ColumnPreviewResponse>({
    queryKey: ['datasets', datasetId, 'columns', 'preview', limit],
    queryFn: async () =>
      (
        await api.get<ColumnPreviewResponse>(
          `/datasets/${datasetId}/columns/preview`,
          { params: { limit } },
        )
      ).data,
    enabled: Boolean(datasetId),
    ...options,
  });
}

// === Mutations ===

export function useUploadDataset(opts?: {
  /** Phase 2.5: реальный XHR upload progress (bytes loaded / total). */
  onUploadProgress?: (progressEvent: AxiosProgressEvent) => void;
}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      projectId,
      file,
      name,
      signal,
    }: {
      projectId: string;
      file: File;
      name?: string;
      /** Phase 2.7: AbortController.signal — опционально. Передаётся
       *  в axios.post; axios 1.7 корректно отменяет XHR при вызове
       *  controller.abort() (axios отличает AbortError от ошибки сети). */
      signal?: AbortSignal;
    }) => {
      const form = new FormData();
      form.append('file', file);
      if (name) form.append('name', name);
      return (
        await api.post<UploadResponse>(
          `/projects/${projectId}/datasets/upload`,
          form,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: opts?.onUploadProgress,
            signal,
          },
        )
      ).data;
    },
    onSuccess: (data, vars) => {
      qc.invalidateQueries({ queryKey: ['datasets', 'project', vars.projectId] });
      qc.setQueryData(['datasets', data.dataset_id], (prev: unknown) => prev);
    },
  });
}

export function useSelectSheet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      datasetId,
      selectedSheet,
      reparse = true,
    }: {
      datasetId: string;
      selectedSheet: string;
      reparse?: boolean;
    }) =>
      (
        await api.patch<Dataset>(`/datasets/${datasetId}/sheets`, null, {
          params: { selected_sheet: selectedSheet, reparse },
        })
      ).data,
    onSuccess: (data) => {
      // Block 5 / Шаг 1: при выборе листа сервер пересчитывает файл (reparse).
      // Явно дёргаем все куски интерфейса, которые зависят от колонок/строк,
      // иначе они держат старые данные и таблица «мигает» (B5 — пропадает таблица).
      qc.invalidateQueries({ queryKey: ['datasets', data.id] });
      qc.invalidateQueries({ queryKey: ['datasets', data.id, 'preview'] });
      qc.invalidateQueries({ queryKey: ['datasets', data.id, 'sheets'] });
      qc.invalidateQueries({ queryKey: ['datasets', data.id, 'columns', 'preview'] });
      qc.invalidateQueries({ queryKey: ['datasets', 'project', data.project_id] });
    },
  });
}

export function useRenameColumns() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      datasetId,
      renames,
    }: {
      datasetId: string;
      renames: Record<string, string>;
    }) =>
      (
        await api.patch<Dataset>(`/datasets/${datasetId}/columns`, { renames })
      ).data,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['datasets', data.id] });
      qc.invalidateQueries({ queryKey: ['datasets', data.id, 'columns'] });
    },
  });
}

export function useSelectColumns() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      datasetId,
      selectedColumns,
    }: {
      datasetId: string;
      selectedColumns: string[];
    }) =>
      (
        await api.patch<Dataset>(`/datasets/${datasetId}/selected-columns`, {
          selected_columns: selectedColumns,
        })
      ).data,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['datasets', data.id] });
      qc.invalidateQueries({ queryKey: ['datasets', data.id, 'columns'] });
    },
  });
}

export function useReparseDataset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (datasetId: string) =>
      (await api.post<Dataset>(`/datasets/${datasetId}/reparse`)).data,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['datasets', data.id] });
      qc.invalidateQueries({ queryKey: ['datasets', data.id, 'preview'] });
      qc.invalidateQueries({ queryKey: ['datasets', 'project', data.project_id] });
    },
  });
}

export function useDeleteDataset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (datasetId: string) => {
      await api.delete(`/datasets/${datasetId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['datasets'] });
    },
  });
}
